import type BaseModel from "./models/BaseModel";
import type BaseObject from "./models/BaseObject";
import type Cabinet from "./models/Cabinet";
import type Channel from "./models/Channel";
import { ChannelType, channelTypeInfo } from "./models/ChannelType";
import type Consumer from "./models/Consumer";
import type Room from "./models/Room";
import type TerminalStrip from "./models/TerminalStrip";
import { noItx } from "./translationItx/itx";
import type Device from "./models/Device";
import type Actor from "./models/Actor";
import { PrefixMode } from "./models/SettingsEnums";
import ELCB from "./models/ELCB";

export default class NameAndTagManager {
  public get className(): string {
    return "NameAndTagManager"; // no itx
  }

  //////////////// settings

  numberOfFixedDigits = 1;

  /** Finds the structural location of an object without exposing grouping nodes in its tag. */
  private getParentOutsideGroups(obj: BaseObject): BaseModel | undefined {
    let parent = obj.parent as BaseModel | undefined;
    while (parent?.isGroup) parent = parent.parent as BaseModel | undefined;
    return parent;
  }

  /// state is not defined error warning-> because of this function
  private getPrefixChars(): Map<ChannelType | string, string> {
    const res = new Map<ChannelType | string, string>([
      [ChannelType.Switch, state.settings.consumerPrefix.switch],
      [ChannelType.Dimmer, state.settings.consumerPrefix.dimming],
      [ChannelType.StaircaseLight, state.settings.consumerPrefix.staircaseLight],
      [ChannelType.TuneableWhite, state.settings.consumerPrefix.tw],
      [ChannelType.Rgb, state.settings.consumerPrefix.rgb],
      //  [ChannelType.Rgbw, state.settings.consumerPrefix.rgbw],
      [ChannelType.Heating, state.settings.consumerPrefix.heating],
      [ChannelType.Jalousie, state.settings.consumerPrefix.jalousie],
      [ChannelType.Blind, state.settings.consumerPrefix.blind],
      [ChannelType.PlugSwitchable, state.settings.consumerPrefix.switchOutlet],
      [ChannelType.Plug, state.settings.consumerPrefix.outlet],
      [ChannelType.Lock, state.settings.consumerPrefix.input],
      [ChannelType.Lan, state.settings.consumerPrefix.lan],
      [ChannelType.Lan2, state.settings.consumerPrefix.lan2],
      [ChannelType.plug_230V, state.settings.consumerPrefix.outlet],
      [ChannelType.plug_Switch, state.settings.consumerPrefix.switchOutlet],
      [ChannelType.plug_Switch1, state.settings.consumerPrefix.switchOutlet],
      [ChannelType.plug_Switch2, state.settings.consumerPrefix.switchOutlet],
      [ChannelType.plug_Switch3, state.settings.consumerPrefix.switchOutlet],
      [ChannelType.plug_LAN, state.settings.consumerPrefix.lan],
      [ChannelType.plug_LAN2, state.settings.consumerPrefix.lan2],
      [ChannelType.plug_USB, state.settings.consumerPrefix.outlet],
      [ChannelType.plug_TV, state.settings.consumerPrefix.outlet],
      [ChannelType.IntermediateDistribution, state.settings.consumerPrefix.intermediateDistribution],

      [ChannelType.suply12V, state.settings.consumerPrefix.s12],
      [ChannelType.suply24V, state.settings.consumerPrefix.s24],
      [ChannelType.suply230V, state.settings.consumerPrefix.e230],
      [ChannelType.suply400V, state.settings.consumerPrefix.e400],

      ["", state.settings.consumerPrefix.e230],

      ["Actor", state.settings.devicePrefix.actor], // no itx
      ["Button", state.settings.devicePrefix.button], // no itx
      ["Sensor", state.settings.devicePrefix.sensor], // no itx
      ["DecentralActor", state.settings.devicePrefix.decentralActor], // no itx
      ["ELCB", state.settings.devicePrefix.elcb], // no itx
      ["Fuse", state.settings.devicePrefix.fuse], // no itx
      ["TerminalStrip", state.settings.devicePrefix.terminalStrip], // no itx
      ["Terminal", state.settings.devicePrefix.terminal], // no itx
      ["Multiplexer", state.settings.devicePrefix.multiplexer], // no itx
    ]);
    return res;
  }

  public getPrefixForChannelType(channelType: ChannelType): string {
    return this.getPrefixChars().get(channelType) || "";
  }

  //////////////////////////////////////

  private indexToTag(prefix: string, index: number): string {
    return prefix + index.toString().padStart(this.numberOfFixedDigits, "0");
  }

  private getAvailableTag(obj: BaseModel, prefix: string, itemlist: Array<BaseModel>, keepExisting?: boolean): string {
    const allOtherUsedTags = this.getAllOtherUsedTags(obj, prefix, itemlist);

    const size = obj.tagSize;
    let index = obj.tagIndex;

    if (keepExisting) {
      let found = false;
      if (index > 0) {
        for (let i = index; i < index + size; i++) {
          found ||= allOtherUsedTags.includes(i);
        }
      } else {
        // todo: implement pure string match
      }

      if (!found) {
        return obj.tag;
      }
    }

    if (allOtherUsedTags.length > 0) {
      index = allOtherUsedTags[allOtherUsedTags.length - 1] + 1;
    } else {
      index = 1;
    }

    if (size == 2) {
      return `${prefix}${index},${index + 1}`;
    } else if (size > 2) {
      return `${prefix}${index}-${index + size - 1}`;
    }

    return `${prefix}${index}`;
  }

  private getNextTagAcrossObjects(obj: BaseModel, prefix: string, itemlist: BaseModel[], keepExisting?: boolean): string {
    const otherObjects = itemlist.filter((item) => item !== obj);
    if (keepExisting && obj.tagIndex > 0 && !otherObjects.some((item) => {
      const index = item.tagIndex;
      return index > 0 && obj.tagIndex < index + item.tagSize && index < obj.tagIndex + obj.tagSize;
    })) {
      return obj.tag;
    }

    const index = otherObjects.reduce((max, item) => {
      const itemIndex = item.tagIndex;
      return itemIndex > 0 ? Math.max(max, itemIndex + item.tagSize - 1) : max;
    }, 0) + 1;
    return this.indexToTag(prefix, index);
  }

  getAvailableTagWithBracetsIfNeeded(tag: string, itemlist: Array<string>): string {
    let count = 0;
    itemlist.forEach((item) => {
      if (item === tag) {
        count++;
      } else if (item.startsWith(tag + "(") && item.endsWith(")")) {
        const numberPart = parseInt(item.slice(tag.length + 1, -1));
        if (!isNaN(numberPart)) {
          count++;
        }
      }
    });
    if (count === 0) {
      return tag;
    }
    return `${tag}(${count})`;
  }

  generateTag(obj: BaseModel, keepExisting?: boolean): string {
    function getTagFromClassName(obj: BaseObject): string {
      let res = state.translate(`components.${obj.className}`, obj.className).substring(0, 1);
      let i = 1;
      let group = obj.getParentChildrenGroup();
      if (state.projectMetadata.uniqueTagsForProject) {
        group = state.getAll(obj.className)?.sort((a, b) => a.tag.localeCompare(b.tag, undefined, { numeric: true }));
      }
      if (!group) {
        return `${res}${i}`;
      }
      for (const v of group) {
        if (v.tag == `${res}${i}` && v != obj) {
          i++;
        }
      }

      return `${res}${i}`;
    }

    // if (obj.tag == "") {
    //   (obj as BaseModel)?.generateTagFromName();
    // }

    // const listWithoutSelf = getParentListWithoutOwn();
    // if (obj.tag == "" && listWithoutSelf) {
    //   obj.tag = this.getAvailableTag(
    //     obj,
    //     state.translate(`components.${obj.className}`, obj.className).substring(0, 1),
    //     listWithoutSelf,
    //   );
    // }

    if (true) {
      /// state.projectMetadata.uniqueTagsForProject) {
      switch (obj.className) {
        case "Consumer": // no itx
          return this.getAvailableTag(
            obj,
            this.getPrefixChars().get((obj as Consumer).prefixType) || "",
            state.projectMetadata.uniqueTagsForProject
              ? state.allConsumers()
              : (obj.parent as Room).consumers.asArray(),
            keepExisting,
          );
        case "Actor": // no itx
          let actorCollection = new Array<Actor>();
          if (state.projectMetadata.uniqueTagsForProject) {
            actorCollection = state.allActors().filter((actor) => !channelTypeInfo(actor.channelType).isSupply);
          } else {
            actorCollection = (obj.parent as Cabinet).actors
              .asArray()
              .filter((actor) => !channelTypeInfo(actor.channelType).isSupply);
          }
          return this.getAvailableTag(
            obj,
            this.getPrefixChars().get("Actor") || "?", // no itx
            actorCollection,
            keepExisting,
          );
        case "Button": // no itx
          let prefix = this.getPrefixChars().get("Button") || "?"; // no itx
          if (obj.isSensor) {
            prefix = this.getPrefixChars().get("Sensor") || "?"; // no itx
          } else if (obj.isDecentralActor) {
            prefix = this.getPrefixChars().get("DecentralActor") || "?"; // no itx
          }
          return this.getAvailableTag(
            obj,
            prefix,
            state.projectMetadata.uniqueTagsForProject ? state.allButtons() : (obj.parent as Room).buttons.asArray(),
            keepExisting,
          );
        case noItx("Fuse"): {
          const fusePrefix = state.settings.devicePrefix.fuse;
          switch (state.settings.fuseTagSchema) {
            case "A":
              return this.getAvailableTag(
                obj,
                fusePrefix,
                state.allFuses(),
                keepExisting,
              );
            case "B":
              if (obj.parent?.className === noItx("ELCB")) {
                const elcbNumber = obj.parent.tag.replace(/^\D+/, "");
                return this.getAvailableTag(
                  obj,
                  `${fusePrefix}${elcbNumber}.`,
                  (obj.parent as ELCB).fuses.asArray(),
                  keepExisting,
                );
              }
              {
                const cabinet = obj.parent as Cabinet;
                const cabinetFuses = cabinet.fi_ls_s.asArray();
                const standaloneFuses = state.projectMetadata.uniqueTagsForProject
                  ? state.allFuses().filter((item) => item.is_FI_LS)
                  : cabinetFuses;
                return this.getAvailableTag(obj, fusePrefix, standaloneFuses, keepExisting);
              }
            case "C":
              {
                const numberedObjects = [
                  ...state.allELCBs(),
                  ...state.allFuses(),
                ];
                return this.getNextTagAcrossObjects(obj, fusePrefix, numberedObjects, keepExisting);
              }
          }
        }
      }
    }

    switch (obj.className) {
      case "Cabinet": // no itx
        if ((keepExisting && state.cabinets.size > 1) || (!keepExisting && state.cabinets.size >= 1)) {
          return getTagFromClassName(obj);
        }

        return obj.tag;
      case "Room": // no itx
      case "Floor": // no itx
        return obj.tag == "" ? getTagFromClassName(obj) : obj.tag;
      case noItx("Fuse"): {
        if (obj.parent?.className == noItx("ELCB")) {
          return this.getAvailableTag(
            obj,
            `${obj.parent.tag}.`,
            (obj.parent as ELCB).fuses.asArray(),
            keepExisting,
          );
        }

        const cabinet = obj.parent?.parent as Cabinet;
        return this.getAvailableTag(
          obj,
          state.settings.devicePrefix.fuse,
          cabinet.fi_ls_s.asArray(),
          keepExisting,
        );
      }

      case noItx("ELCB"): {
        if (state.settings.fuseTagSchema === "C") {
          const numberedObjects = [
            ...state.allELCBs(),
            ...state.allFuses(),
          ];
          return this.getNextTagAcrossObjects(
            obj,
            state.settings.devicePrefix.elcb,
            numberedObjects,
            keepExisting,
          );
        }
        const cabinet = obj.parent as Cabinet;
        return this.getAvailableTag(
          obj,
          this.getPrefixChars().get("ELCB") || "?", // no itx
          cabinet.elcbs.asArray(),
          keepExisting,
        );
      }
      case noItx("Terminal"): {
        const termialStrip = obj.parent as TerminalStrip;
        return this.getAvailableTag(
          obj,
          this.getPrefixChars().get("Terminal") || "", // == "" terminal have only numbers no prefix  // no itx
          termialStrip.terminals.asArray(),
          keepExisting,
        );
      }
      case noItx("TerminalStrip"):
        return this.getAvailableTag(
          obj,
          this.getPrefixChars().get("TerminalStrip") || "?", // no itx
          (obj.parent as Cabinet).terminalStrips.asArray(),
          keepExisting,
        );
      case "Multiplexer":
        return this.getAvailableTag(
          obj,
          this.getPrefixChars().get("Multiplexer") || "?",
          state.sceneGroup.asArray(),
          keepExisting,
        );
    }

    console.log(`uniqueTagsForProject== false not supported`);
    return obj.tag;
  }

  getFullTag(obj: BaseObject): string | undefined {
    if (obj.className === "Fuse") {
      if (state.settings.includeElcbTagInFuseFullTag && obj.parent?.className === "ELCB") {
        const parentTag = obj.parent.fullTag;
        return parentTag ? `${parentTag}.${obj.tag}` : obj.tag;
      }
      return obj.tag;
    }

    if (state.projectMetadata.uniqueTagsForProject) {
      let res = obj.tag;

      if (obj.className == "Signal") {
        const channel = obj.parent as any as Channel;
        const device = channel.parent as Device;

        let postfix = device?.parent?.fullTag;

        if (postfix != undefined && channel) {
          if (channel.knxChannel) {
            // return `${channel.parent?.fullTag}${channel.getTagPrefix()}${channel.shortName2}${obj.getTagPrefix()}${obj.tag} (${postfix})`;

            // MWE 2.2.2025
            // ignore ChqnnelTag, because in the explorer we show the channel tag as part of the icon
            //
            // if this is bad for any other usage,
            // we have to add a usage parameter in the function
            //
            if (state.settings.prefixMode == PrefixMode.DIN) {
              return `${device?.getTagPrefix()}${device?.tag}${obj.getTagPrefix()}${obj.tag} (${postfix})`;
            } else {
              return `${device?.tag}${obj.getTagPrefix()}${obj.tag} (${postfix})`;
            }
          }
        } else {
          return obj.tag;
        }
      } else if (obj.className == "Consumer" || obj.className == "Button") {
        const postfix = this.getParentOutsideGroups(obj)?.fullTag;
        if (postfix) {
          return `${obj.tag} (${postfix})`;
        } else {
          return obj.tag;
        }
      } else if (obj.className == "ButtonChannel") {
        const channel = obj as Channel;
        let res = obj.parent?.tag || "";
        if (channel.isRequestChannel) {
          if (channel.knxChannel) {
            res += "." + channel.knxChannel.shortName;
          } else {
            res += "." + channel.name;
          }
        }

        let postfix: string | undefined;
        if (obj.parent) {
          postfix = this.getTreeTagPostfixText(obj.parent);
        }

        if (postfix) {
          return res + postfix;
        } else {
          return res;
        }
      }
    } else return undefined;
  }

  getAllOtherUsedTags(obj: BaseObject, prefix: string, itemlist: Array<BaseModel>): Array<number> {
    const fliteredItemlist = itemlist.filter((i) => i != obj && i.tag.startsWith(prefix));

    const res = new Array<number>();
    fliteredItemlist.forEach((item) => {
      const size = item.tagSize;
      const index = item.tagIndex;

      //
      // index ist < 0 zb für "EG"
      // index = 10 zB für F10 und F10-12
      //
      if (index > 0) {
        for (let i = index; i < index + size; i++) {
          res.push(i);
        }
      }
    });

    return res.sort((a, b) => {
      return a - b;
    });
  }

  getRelativeTag(obj: BaseObject, relativeObject: BaseModel): string | undefined {
    if (state.projectMetadata.uniqueTagsForProject) {
      let res = obj.tag;
      if (obj.className == "Consumer" || obj.className == "Button") {
        const postfix = this.getParentOutsideGroups(obj)?.fullTag;
        if (postfix) {
          return `${obj.tag} (${postfix})`;
        } else {
          return obj.tag;
        }
      }
    }

    return undefined;
  }

  getTreeTagPrefix(obj: BaseObject, className?: string): string | any | undefined {
    let res = obj.parent?.fullTag;
    if (res != "") {
      res += obj.tagPrefix;
    }

    if (obj.className == "Room" || obj.className == "Actor" || obj.className == "Channel") {
      return (
        <div className={className} style={{ fontWeight: "normal" }}>
          {res}
        </div>
      );
    }

    if (state.projectMetadata.uniqueTagsForProject) {
      if (obj.className == "ButtonChannel") {
        res = obj.parent?.tag + obj.tagPrefix;
        return (
          <div className={className} style={{ fontWeight: "normal" }}>
            {res}
          </div>
        );
      }
    }

    return undefined;
  }

  getTreeTagPostfix(obj: BaseObject, className?: string): string | any | undefined {
    return (
      <div className={className} style={{ fontWeight: "normal" }}>
        {this.getTreeTagPostfixText(obj)}
      </div>
    );
  }

  private getTreeTagPostfixText(obj: BaseObject): string | any | undefined {
    if (state.projectMetadata.uniqueTagsForProject) {
      if (obj.className == "Consumer" || obj.className == "Actor" || obj.className == "Button") {
        const fulltag = this.getParentOutsideGroups(obj)?.fullTag;
        if (fulltag) {
          return ` (${fulltag})`;
        } else {
          return undefined;
        }
      } else if (obj.className == "ButtonChannel") {
        let fulltag = obj.parent?.parent?.fullTag || "";
        if (fulltag != "") {
          return ` (${fulltag})`;
        } else {
          return undefined;
        }
      }
    } else {
      return undefined;
    }
  }

  modifyAllTagsToNewTagSystem(newValue: boolean) {
    const oldTags = new Map<string, string>();

    //
    // Phase I alle Tags löschen
    //
    state.allConsumers().forEach((consumer) => {
      oldTags.set(consumer.id, consumer.tag);
      consumer.tag = "";
    });

    state.allConsumers().forEach((consumer) => {
      consumer.tag = this.generateTag(consumer, false);
      consumer.raiseOnChange("tag");
    });
  }

  public renumberTagObjs(objs: Array<BaseModel>, prefix: string) {
    let index = 1;
    objs.forEach((obj) => {
      const size = obj.tagSize;
      let OldTag = obj.tag;
      obj.tag = this.indexToTag(prefix, index);
      obj.raiseOnChange("tag");
      obj.parent?.raiseOnChange("childCollection");
      index += size;
    });
  }

  private renumberElectricalObjects(objs: BaseModel[], getPrefix: (obj: BaseModel) => string) {
    let index = 1;
    objs.forEach((obj) => {
      const size = obj.tagSize;
      const prefix = getPrefix(obj);
      let tag = `${prefix}${index}`;
      if (size === 2) {
        tag += `,${prefix}${index + 1}`;
      } else if (size !== 1) {
        tag += `-${prefix}${index + size - 1}`;
      }
      obj.tag = tag;
      obj.raiseOnChange("tag");
      obj.parent?.raiseOnChange("childCollection");
      index += size;
    });
  }

  public renumberFuseTags(elcb?: ELCB, orderedFuses?: BaseModel[]) {
    if (state.settings.fuseTagSchema === "A") {
      this.renumberElectricalObjects(
        state.allFuses().sort((a, b) => a.tag.localeCompare(b.tag, undefined, { numeric: true })),
        () => state.settings.devicePrefix.fuse,
      );
      return;
    }

    if (state.settings.fuseTagSchema === "B") {
      if (elcb) {
        this.renumberElectricalObjects(
          orderedFuses ?? elcb.fuses.sortBy("tag"),
          () => `${state.settings.devicePrefix.fuse}${elcb.tag.replace(/^\D+/, "")}.`,
        );
      } else {
        const standaloneFuses = state.allFuses()
          .filter((fuse) => fuse.is_FI_LS)
          .sort((a, b) => a.tag.localeCompare(b.tag, undefined, { numeric: true }));
        if (state.projectMetadata.uniqueTagsForProject) {
          this.renumberElectricalObjects(standaloneFuses, () => state.settings.devicePrefix.fuse);
        } else {
          state.cabinets.forEach((cabinet) => {
            this.renumberElectricalObjects(
              cabinet.fi_ls_s.sortBy("tag"),
              () => state.settings.devicePrefix.fuse,
            );
          });
        }
        state.allELCBs().forEach((currentElcb) => {
          this.renumberElectricalObjects(
            currentElcb.fuses.sortBy("tag"),
            () => `${state.settings.devicePrefix.fuse}${currentElcb.tag.replace(/^\D+/, "")}.`,
          );
        });
      }
      return;
    }

    const compareByTagNumber = (a: BaseModel, b: BaseModel) => {
      const aIndex = a.tagIndex > 0 ? a.tagIndex : Number.MAX_SAFE_INTEGER;
      const bIndex = b.tagIndex > 0 ? b.tagIndex : Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex || a.tag.localeCompare(b.tag, undefined, { numeric: true });
    };
    const objects = [...state.allELCBs(), ...state.allFuses()].sort(compareByTagNumber);
    this.renumberElectricalObjects(objects, (obj) =>
      obj.className === noItx("ELCB")
        ? state.settings.devicePrefix.elcb
        : state.settings.devicePrefix.fuse,
    );
  }

  public renumberElcbs() {
    const elcbs = state.allELCBs().sort((a, b) => a.tag.localeCompare(b.tag, undefined, { numeric: true }));
    this.renumberElectricalObjects(elcbs, () => state.settings.devicePrefix.elcb);
  }
}
